<template>
    <v-banner
        v-if="needRefresh"
        class="tw-z-[1005]"
        color="primary"
        density="compact"
        elevation="24"
        location="bottom left"
        position="fixed">
        <template #text> {{ $t('promptUpdate.text') }} </template>
        <template #actions>
            <v-btn
                height="2rem"
                @click="close"
                >{{ $t('promptUpdate.later') }}</v-btn
            >
            <v-btn
                class="tw-ml-2"
                color="primary"
                height="2rem"
                variant="flat"
                @click="onUpdateClick"
                >{{ $t('promptUpdate.update') }}</v-btn
            >
        </template>
    </v-banner>

    <!-- Update Available Dialog -->
    <v-dialog
        v-model="showUpdateDialog"
        max-width="400"
        persistent>
        <v-card>
            <v-card-title class="tw-flex tw-items-center tw-gap-2">
                <v-icon color="primary">mdi-update</v-icon>
                {{ $t('promptUpdate.title') }}
            </v-card-title>
            <v-card-text>
                {{ $t('promptUpdate.dialogMessage') }}
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    variant="text"
                    @click="dismissUpdate">
                    {{ $t('promptUpdate.later') }}
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    @click="onUpdateClick">
                    {{ $t('promptUpdate.update') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
    import { isSafeMode } from '@/utils/safeMode';
    import { useRegisterSW } from 'virtual:pwa-register/vue';

    const showUpdateDialog = ref(false);

    // On iOS or safe mode, skip immediate update check to avoid reload loop.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const { offlineReady, needRefresh, updateServiceWorker } = useRegisterSW({
        immediate: true,
        async onRegisteredSW(swUrl, r) {
            if (!isIOS && !isSafeMode() && r) {
                await r.update();
            }
        },
    });

    function onUpdateClick() {
        // In safe mode, update SW without reloading the page
        updateServiceWorker(isSafeMode() ? false : true);
    }

    // Watch for updates and show dialog instead of alert
    watch(needRefresh, (newValue) => {
        if (newValue) {
            showUpdateDialog.value = true;
        }
    }, { immediate: true });

    async function close() {
        offlineReady.value = false;
        needRefresh.value = false;
    }

    async function dismissUpdate() {
        showUpdateDialog.value = false;
        await close();
    }
</script>

<style scoped></style>
